import DOMPurify from "dompurify";

export interface SanitizeOptions {
  allowExternalImages?: boolean;
  attachments?: Array<{
    filename: string;
    contentType: string;
    contentId?: string;
    contentBase64?: string;
  }>;
}

export interface SanitizeResult {
  html: string;
  hasExternalImages: boolean;
}

export function sanitizeHtml(
  html: string,
  options: SanitizeOptions = {}
): SanitizeResult {
  if (typeof window === "undefined" || !html) {
    return { html: html || "", hasExternalImages: false };
  }

  let processedHtml = html;

  // 1. Resolve inline CID images from attachments
  if (options.attachments && options.attachments.length > 0) {
    for (const att of options.attachments) {
      if (att.contentBase64) {
        const dataUri = `data:${att.contentType || "image/png"};base64,${att.contentBase64}`;

        if (att.contentId) {
          const cleanCid = att.contentId.replace(/^<|>$/g, "");
          const cidRegex = new RegExp(`cid:<?${escapeRegExp(cleanCid)}>?`, "gi");
          processedHtml = processedHtml.replace(cidRegex, dataUri);
        }

        if (att.filename) {
          const filenameRegex = new RegExp(`cid:<?${escapeRegExp(att.filename)}>?`, "gi");
          processedHtml = processedHtml.replace(filenameRegex, dataUri);
        }
      }
    }
  }

  // 2. Core sanitation vía DOMPurify (protege de mXSS y ejecución de scripts).
  //    Se bloquean elementos activos (svg/math, iframe, form, etc.), atributos
  //    de eventos y CSS (evita exfiltración vía background-image/url()).
  const clean = DOMPurify.sanitize(processedHtml, {
    USE_PROFILES: { html: true, svg: false, svgFilters: false, mathMl: false },
    FORBID_TAGS: [
      "iframe",
      "object",
      "embed",
      "form",
      "input",
      "select",
      "textarea",
      "button",
      "video",
      "audio",
      "source",
      "track",
      "style",
      "link",
      "meta",
      "base",
    ],
    FORBID_ATTR: ["style", "formaction", "xlink:href"],
    ALLOW_DATA_ATTR: false,
  });

  // 3. Re-parsing ligero para detectar imágenes externas y normalizar enlaces
  const doc = new DOMParser().parseFromString(clean, "text/html");
  let hasExternalImages = false;

  function finalizeNode(node: Node) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      const tag = el.tagName.toLowerCase();

      // Los enlaces se abren en pestaña nueva con noopener
      if (tag === "a") {
        const href = el.getAttribute("href") || "";
        if (href && !href.startsWith("#")) {
          el.setAttribute("target", "_blank");
          el.setAttribute("rel", "noopener noreferrer");
        } else {
          el.removeAttribute("target");
          el.removeAttribute("rel");
        }
      }

      if (tag === "img") {
        const rawSrc = el.getAttribute("src") || "";
        const isExternal =
          rawSrc.startsWith("http://") ||
          rawSrc.startsWith("https://") ||
          rawSrc.startsWith("//");

        // srcset permite rastrear aunque src esté bloqueado
        el.removeAttribute("srcset");

        if (isExternal) {
          hasExternalImages = true;
          if (!options.allowExternalImages) {
            el.setAttribute("data-blocked-src", rawSrc);
            el.setAttribute(
              "src",
              "data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=="
            );
            el.classList.add("email-blocked-image");
          }
        }
      }

      Array.from(el.children).forEach(finalizeNode);
    }
  }

  finalizeNode(doc.body);

  return {
    html: doc.body.innerHTML,
    hasExternalImages,
  };
}

export function sanitizeText(text: string): string {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}